import type { TFunction } from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listProjectPeople } from "../../../api/access/accessApi";
import type { AccessPerson } from "../../../api/access/accessModel";
import { useSession } from "../../../session/sessionContext";
import { useLoad } from "../../../shared/loading/useLoad";

export interface MemberNames {
  /** Everyone the project's groups reach (accounts.md GS-1). */
  readonly members: readonly AccessPerson[];
  /** The member's display name; "you" for the signed-in user; "Unknown person" if unknown. */
  readonly nameOf: (userId: string) => string;
  readonly currentUserId: string | null;
}

const noMembers = (): Promise<readonly AccessPerson[]> => Promise.resolve([]);

/** A user ID as the screen names it: "you", the member's name, or "Unknown person", never part of the ID (row 15 of
 * the vocabulary review, 3 Nov 2026). */
export function memberName(
  userId: string,
  people: { readonly currentUserId: string | null; readonly names: ReadonlyMap<string, string> },
  t: TFunction,
): string {
  return userId === people.currentUserId ? t("threads.people.you") : (people.names.get(userId) ?? t("threads.people.unknown"));
}

/**
 * Names for the user IDs entries carry (authors, hosts, offers). A member list that fails
 * to load is not an error worth a notice here: its people read "Unknown person" instead.
 */
export function useMemberNames(projectId: string | null): MemberNames {
  const { t } = useTranslation();
  const { session } = useSession();
  const currentUserId = session.status === "signedIn" ? session.session.account.userId : null;
  const load = useCallback(
    (signal: AbortSignal) => (projectId === null ? noMembers() : listProjectPeople(projectId, signal)),
    [projectId],
  );
  const { state } = useLoad(load);
  const members = useMemo(() => (state.status === "loaded" ? state.value : []), [state]);

  return useMemo(() => {
    const names = new Map(members.map((member) => [member.userId, member.displayName]));
    return { members, currentUserId, nameOf: (userId: string) => memberName(userId, { currentUserId, names }, t) };
  }, [members, currentUserId, t]);
}
